<template>
    <div>
        <form class="w-full max-w-xs md:max-w-md md:ml-auto my-6" @submit.prevent="validate('expiration')">
            <div class="md:flex md:items-center">
                <input v-model="discountCode"
                       type="text"
                       class="bg-gray-200 appearance-none border-2 border-gray-200 rounded-md w-full py-2 px-3 md:px-4 text-gray-700 leading-tight focus:outline-none focus:bg-white focus:border-blue-500 disabled:bg-leonard-grey disabled:!border-transparent"
                       :class="{'is_invalid': invalid}"
                       name="discount"
                       @change="invalid === true ? invalid = false : null"
                       :disabled="disabled || validated"
                       placeholder="Enter discount code"
                >
                <div class="mt-2 md:mt-0 text-nowrap">
                    <button v-show="!validated"
                            type="submit"
                            :disabled="disabled || loading"
                            class="w-full rounded-md py-2 px-3 md:px-4 bg-blue-600 hover:bg-blue-700 text-sm text-white md:ml-2 font-bold disabled:bg-gray-500 disabled:text-gray-300 disabled:cursor-not-allowed"
                    >
                        <font-awesome-icon icon="spinner" spin v-if="loading"></font-awesome-icon> <span v-else>Apply Discount</span>
                    </button>
                    <button
                        v-show="validated"
                        type="button"
                        class="w-full rounded-md py-2 px-3 md:px-4 bg-red-700 hover:bg-red-600 text-sm text-white md:ml-2 font-bold disabled:bg-gray-500 disabled:text-gray-300 disabled:cursor-not-allowed"
                        @click.prevent="remove"
                        :disabled="disabled"
                    >
                        Remove Discount
                    </button>
                </div>
            </div>
            <p v-if="invalid" class="mt-1 text-red-600 text-sm italic">An error occurred. Please check your discount code and try again.</p>
        </form>
        <DiscountExpirationModal ref="discountExpirationModal" @remove="remove" />
    </div>
</template>

<script>
    import Cart from '../../../classes/Cart';
    import DiscountExpirationModal from "../../../components/app/DiscountExpirationModal.vue";

    export default {
        props: ['discount', 'disabled', 'subtotal', 'isSenior', 'makePayment'],

        data() {
            return {
                Cart: new Cart(),
                message: '',
                discountCode: null,
                oneTimeCode: false,
                expired: null,
                validated: false,
                invalid: false,
                loading: false,
            }
        },

        async mounted() {
            await this.checkDiscount();

            if (this.expired) {
                this.handleExpiredDiscount();
            }
        },

        components: {
            DiscountExpirationModal,
        },

        methods: {
            resetData() {
                this.discountCode = null;
                this.validated = false;
                this.invalid = false;
                this.expired = null;
                feedback('Data has been reset.', 'info');
            },

            async validate(type = '') {
                this.$emit('update:makePayment', false);

                const data = {
                    code: this.discountCode,
                    subtotal: this.subtotal,
                    isSenior: this.isSenior,
                    isEmployee: this.isEmployee,
                    isSession: false,
                };

                this.loading = true;
                await this.Cart.checkDiscount(data)
                    .then((response) => {
                        const data = response.data;
                        this.expired = data.expired;

                        if (type === 'expiration' && this.expired) {
                            this.handleExpiredDiscount('button');
                            return;
                        }

                        data.valid ? this.handleValidDiscount(data) : this.handleInvalidDiscount(response.message);
                    })
                    .catch((error) => {
                        console.error('An issue occurred while verifying the code.', error);
                    })
                    .finally(() => {
                        this.loading = false;
                    });
            },

            async triggerDiscountCheck() {
                await this.checkDiscount();

                if (this.expired) {
                    this.handleExpiredDiscount();
                }

                if (this.invalid && !this.expired) {
                    this.resetData();
                }
            },

            remove() {
                this.resetData();
                this.$emit('update:makePayment', true);
                this.$emit('remove');
            },

            handleExpiredDiscount(trigger = null) {
                trigger === 'button'
                    ? this.$refs.discountExpirationModal.showExpiredModalButtonTriggered()
                    : this.$refs.discountExpirationModal.showExpiredModal();

                this.invalid = false;
                this.$emit('update:makePayment', true);
            },

            handleValidDiscount(data) {
                this.validated = true;
                this.$emit('add', data.amount, data.amountType, data.shipping, data.code_id);
            },

            handleInvalidDiscount(message) {
                this.invalid = true;
                if (message && message.length > 0) {
                    feedback(message, 'danger');
                }
                this.$emit('update:makePayment', true);
            },

            async checkDiscount() {
                let user_id = this.$auth.user().details.id;

                await this.Cart.hasDiscount(user_id)
                    .then(async (response) => {
                        if (!response.success) {
                            feedback('Unable to check cart discounts', 'danger');
                            return;
                        }

                        if (!response.data) {
                            console.log(response.message);
                            return;
                        }

                        this.discountCode = response.data.code;
                        if (response.data.code) {
                            await this.validate();
                        }
                    })
                    .catch((error) => {
                        console.error('An error occurred while checking cart discounts:', error);
                        feedback('An unexpected error occurred. Please try again.', 'danger');
                    });
            },
        },
    }
</script>

<style lang="scss" scoped>
    .is_invalid {
        position: relative;
        border: 2px solid red;
        background-image: url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='%23e3342f' viewBox='-2 -2 7 7'%3E%3Cpath stroke='%23e3342f' d='M0 0l3 3m0-3L0 3'/%3E%3Ccircle r='.5'/%3E%3Ccircle cx='3' r='.5'/%3E%3Ccircle cy='3' r='.5'/%3E%3Ccircle cx='3' cy='3' r='.5'/%3E%3C/svg%3E");
        background-repeat: no-repeat;
        background-position: 100% calc(.375em + 0rem);
        background-size: calc(.75em + .375rem) calc(.75em + .75rem);
    }
</style>
